import { useAsync } from "@/shared/hooks";

import { teamPicksLoader } from "../loader/team-picks-loader";
import { teams } from "@/shared/utils/teams";
import {
  ChevronsDownUp,
  ChevronsUpDown,
  CircleHelp,
  CornerDownRight,
  History,
} from "lucide-react";

import {
  flexRender,
  getExpandedRowModel,
  getFilteredRowModel,
} from "@tanstack/react-table";
import {
  Button,
  cn,
  DialogTrigger,
  Dialog,
  Flex,
  Grid,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
  DialogTitle,
  DialogContent,
  DialogDescription,
  Switch,
} from "@suns/design-system";
import { useReactTable } from "@tanstack/react-table";
import { getCoreRowModel } from "@tanstack/react-table";
import { formatMoney, getValueColor } from "@/shared/utils/helper-functions";
import { TeamLogo } from "@/components/TeamLogo/TeamLogo";
import { LeagueId, SunsApiError } from "@suns/api";
// eslint-disable-next-line no-restricted-imports -- DO NOT COPY THIS LINE. See docs/lints.md.
import {
  PickAssetRow,
  PickRow,
  TeamRow,
} from "@suns/api/generated-client/apollo";
import { Pick, PickAssetChart } from "./PickAssetChart";
import React, { useState } from "react";
import { DialogHeader } from "@suns/design-system/src/components/Dialog/Dialog";
import dayjs from "dayjs";
import { ordinal } from "@/shared/utils";
import { Link } from "react-router-dom";

function highestChanceOutcome(pickAsset: PickAssetRow, teamId: number) {
  return pickAsset.picks
    .flatMap((pick) =>
      pick.pickSplits.flatMap((pickSplit) =>
        pickSplit.pickOutcomes
          .filter((pickOutcome) => pickOutcome.teamId === teamId)
          .map((pickOutcome) => ({
            projectedPick: pick.projectedPickNumber,
            chance: pickOutcome.chance,
          }))
      )
    )
    .reduce(
      (best, current) => {
        if (current.chance == null) return best;
        if (best.chance == null || current.chance > best.chance) return current;
        return best;
      },
      {
        projectedPick: null,
        chance: null,
      }
    );
}

export function TeamPicks({ team }: { team: TeamRow }) {
  const {
    response: pickAssets,
    loading,
    error,
  } = useAsync(teamPicksLoader, { teamId: team.id });

  const [hideZeroChance, setHideZeroChance] = useState(true);

  if (error) {
    throw new SunsApiError("Error loading team picks", { cause: error });
  }

  return (
    <Flex direction="down" gap="md">
      <Flex direction="right" gap="sm" align="center">
        <Switch
          checked={hideZeroChance}
          onCheckedChange={() => setHideZeroChance(!hideZeroChance)}
        />
        <Text size="sm">Hide 0% chance assets</Text>
      </Flex>
      <PickTable
        pickAssets={pickAssets ?? []}
        currentTeam={team}
        loading={loading}
        hideZeroChance={hideZeroChance}
        setHideZeroChance={setHideZeroChance}
      />
    </Flex>
  );
}

const PickTable = ({
  pickAssets,
  currentTeam,
  loading,
  hideZeroChance,
  setHideZeroChance,
}: {
  pickAssets: PickAssetRow[];
  currentTeam: TeamRow;
  loading: boolean;
  hideZeroChance: boolean;
  setHideZeroChance: (hideZeroChance: boolean) => void;
}) => {
  const formatAssetMessage = (pickAsset: PickAssetRow) => {
    const assetTeams = pickAsset.picks.map((pick) =>
      teams.find((team) => team.id === pick.teamId)
    );
    switch (pickAsset.type.toUpperCase()) {
      case PickAssetRow.type.SWAP:
        return `Swap involving ${assetTeams
          .map((team) => team?.TEAM_ABBR)
          .join(", ")}`;
      case PickAssetRow.type.FAVORABLE:
        if (!pickAsset.priority) {
          throw new Error(
            `Pick asset priority is required for pick asset ${pickAsset.id}`
          );
        }

        if (pickAsset.priority === 1) {
          return "Most favorable";
        } else if (pickAsset.priority === pickAsset.picks.length) {
          return "Least favorable";
        } else {
          return `${ordinal(pickAsset.priority)} most favorable`;
        }
      case PickAssetRow.type.PROTECTED_PICK:
        return `${assetTeams[0]?.TEAM_ABBR}'s Protected Pick`;
      case PickAssetRow.type.PICK_PROTECTION:
        return `Pick Protection`;
      case PickAssetRow.type.UNPROTECTED_PICK:
        return `Unprotected Pick`;
    }
  };

  const table = useReactTable({
    data: pickAssets,
    columns: [
      {
        header: " ",
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <button
              onClick={row.getToggleExpandedHandler()}
              style={{ cursor: "pointer" }}
            >
              {row.getIsExpanded() ? (
                <div className="mx-3 rounded-full bg-primary p-1">
                  <ChevronsDownUp className="h-4 w-4 text-white" />
                </div>
              ) : (
                <div className="mx-3 rounded-full bg-primary p-1">
                  <ChevronsUpDown className="h-4 w-4 text-white" />
                </div>
              )}
            </button>
          ) : (
            ""
          );
        },
      },

      {
        header: "Year",
        accessorKey: "year",
      },
      {
        header: "Round",
        accessorKey: "round",
      },
      {
        header: "Asset",
        accessorKey: "asset",
        cell: ({ row }) => {
          return formatAssetMessage(row.original);
        },
      },
      {
        header: `Proj. pick`,
        accessorKey: "Pick Projection",
        cell: ({ row }) => {
          const pickProjectionTeam = teams.find(
            (team) => team.id === row.original.picks[0]?.teamId
          );

          return pickProjectionTeam?.TEAM_ABBR ?? "--";
        },
      },
      {
        header: `Proj. #`,
        accessorKey: "projectedPickNumber",
        cell: ({ row }) => {
          return (
            highestChanceOutcome(row.original, currentTeam.id).projectedPick ??
            "--"
          );
        },
      },
      {
        header: "Chance",
        accessorKey: "chance",
        cell: ({ row }) => {
          const highestChance = highestChanceOutcome(
            row.original,
            currentTeam.id
          ).chance;

          return (
            <Text
              className={cn(
                highestChance != null && "font-bold",
                highestChance != null
                  ? highestChance < 50
                    ? "text-red-600"
                    : "text-green-600"
                  : ""
              )}
              size="sm"
              heading
            >
              {highestChance ? `${highestChance}%` : "0%"}
            </Text>
          );
        },
      },
      {
        header: "Value",
        accessorKey: "value",
        cell: ({ row }) => {
          const totalPickValue = row.original.picks
            .flatMap((pick) =>
              pick.pickSplits.flatMap(
                (split) =>
                  split.pickOutcomes.find(
                    (outcome) => outcome.teamId === currentTeam.id
                  )?.value || 0
              )
            )
            .reduce((sum, value) => sum + value, 0);
          return totalPickValue > 0 ? formatMoney(totalPickValue) : "--";
        },
      },
    ],
    state: {
      globalFilter: hideZeroChance,
    },
    getCoreRowModel: getCoreRowModel(),
    getRowCanExpand: () => true,
    getExpandedRowModel: getExpandedRowModel(),
    initialState: {
      expanded: {},
    },
    onGlobalFilterChange: setHideZeroChance,
    globalFilterFn: (row, _columnId, filterValue) => {
      if (!filterValue) return true;
      return row.original.picks.some((pick) =>
        pick.pickSplits.some((split) =>
          split.pickOutcomes.some(
            (outcome) =>
              outcome.teamId === currentTeam.id && outcome.chance !== 0
          )
        )
      );
    },
    getFilteredRowModel: getFilteredRowModel(),
  });

  if (loading) {
    return (
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead key={header.id}>
                    <Text size="sm" heading>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </Text>
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getHeaderGroups().map((headerGroup) =>
            [1, 2, 3, 4, 5].map((i) => (
              <TableRow key={i}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableCell key={header.id}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    );
  }

  if (!pickAssets?.length)
    return (
      <Text muted size="lg" className="py-4 text-center">
        No picks found.
      </Text>
    );

  return (
    <Flex direction="down" gap="md">
      <Flex className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      <Text size="sm" heading>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </Text>
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => {
              return (
                <React.Fragment key={row.id}>
                  <TableRow>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className="whitespace-nowrap p-1"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  {row.getIsExpanded() && (
                    <TableRow>
                      <TableCell colSpan={row.getAllCells().length}>
                        <Flex direction="down" gap="md">
                          <DesktopPickView
                            pickAsset={row.original}
                            currentTeam={currentTeam}
                          />
                          <MobilePickView
                            pickAsset={row.original}
                            currentTeam={currentTeam}
                          />

                          <Flex direction="right" gap="sm" className="pl-4">
                            {row.original.contingentOn && (
                              <Flex direction="right" gap="sm" align="center">
                                <CircleHelp className="h-4 w-4 text-yellow-600" />
                                <Text size="xs">
                                  {row.original.contingentOn}
                                </Text>
                              </Flex>
                            )}
                            {row.original.fallbackOn && (
                              <Flex direction="right" gap="sm" align="center">
                                <CornerDownRight className="h-4 w-4 text-yellow-600" />
                                <Text size="xs">{row.original.fallbackOn}</Text>
                              </Flex>
                            )}
                          </Flex>
                        </Flex>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Flex>
    </Flex>
  );
};

interface PickViewProps {
  pickAsset?: PickAssetRow;
  currentTeam: TeamRow;
}

const pickOutcomeChance = (pick: PickRow, currentTeamId: number) => {
  return pick.pickSplits
    .flatMap((split) => split.pickOutcomes)
    .find((outcome) => outcome.teamId === currentTeamId)?.chance;
};

const compactCurrency = (pick: PickRow) => {
  if (pick.value == null) return "--";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    compactDisplay: "short",
  }).format(pick.value || 0);
};

const pickRoundFormat = (round: number) => {
  if (round === 1) return "First Round";
  if (round === 2) return "Second Round";
};

function DesktopPickView({ pickAsset, currentTeam }: PickViewProps) {
  return (
    <Flex className="max-xl:hidden" direction="right" gap="md">
      <Flex className="w-[50%]" direction="down" gap="lg">
        <Grid columns={"4"} justify="center" align="center" className="">
          <Text size="sm" heading muted className="whitespace-nowrap">
            Teams Pick
          </Text>
          <Text size="sm" heading muted className="whitespace-nowrap">
            Projected
          </Text>
          <Text size="sm" heading muted className="whitespace-nowrap">
            {currentTeam.code} Chance
          </Text>
          <Text size="sm" heading muted className="whitespace-nowrap">
            {currentTeam.code} Value
          </Text>
        </Grid>
        {pickAsset?.picks.map((pick, index) => {
          return (
            <Grid key={index} columns={"4"} justify="center" align="center">
              <Link to={`/teams/${pick.teamId}`}>
                <TeamLogo
                  nbaTeamId={teams.find((t) => t.id === pick.teamId)?.TEAM_ID}
                  leagueId={LeagueId.NBA}
                />
              </Link>
              <Text size="2xl" className="font-semibold text-gray-600">
                #{pick.projectedPickNumber ?? "--"}
              </Text>
              <Text
                size="2xl"
                heading
                className={cn(
                  getValueColor(pickOutcomeChance(pick, currentTeam.id))
                )}
              >
                {pickOutcomeChance(pick, currentTeam.id) ?? "--"}%
              </Text>
              <Text size="2xl" heading className="font-semibold text-gray-600">
                {compactCurrency(pick)}
              </Text>
            </Grid>
          );
        })}
      </Flex>
      <Flex className="w-full" direction="down" gap="lg" justify="start">
        <Text size="sm" heading muted className="text-center">
          Breakdown
        </Text>
        {pickAsset && <PickAssetChart pickAsset={pickAsset} />}
      </Flex>
      <Flex direction="down" gap="lg" justify="start">
        <Text size="sm" heading muted>
          Timeline
        </Text>
        {pickAsset?.picks.map((pick) => {
          return <PickTimeline key={pick.id} pick={pick} />;
        })}
      </Flex>
    </Flex>
  );
}

function MobilePickView({ pickAsset, currentTeam }: PickViewProps) {
  return (
    <Flex
      className="w-full xl:hidden"
      direction="down"
      gap="md"
      justify="start"
    >
      {pickAsset?.picks.map((pick) => (
        <Flex key={pick.id} direction="down" gap="md">
          <Flex direction="down" gap="md">
            <Grid columns={"5"} justify="center" align="center">
              <Text size="sm" heading muted className="whitespace-nowrap">
                Teams Pick
              </Text>

              <Text size="sm" heading muted className="whitespace-nowrap">
                Projected
              </Text>

              <Text size="sm" heading muted className="whitespace-nowrap">
                {currentTeam.code} Chance
              </Text>

              <Text size="sm" heading muted className="whitespace-nowrap">
                {currentTeam.code} Value
              </Text>

              <Text size="sm" heading muted className="whitespace-nowrap">
                Timeline
              </Text>
            </Grid>
            <Grid columns={"5"} justify="center" align="center">
              <TeamLogo
                nbaTeamId={teams.find((t) => t.id === pick.teamId)?.TEAM_ID}
                leagueId={LeagueId.NBA}
              />
              <Text size="2xl" className="font-semibold text-gray-600">
                #{pick.projectedPickNumber ?? "--"}
              </Text>
              <Text
                size="2xl"
                heading
                className={cn(
                  "font-semibold",
                  getValueColor(pickOutcomeChance(pick, currentTeam.id))
                )}
              >
                {pickOutcomeChance(pick, currentTeam.id) ?? "--"}%
              </Text>
              <Text size="2xl" heading className="font-semibold text-gray-600">
                {compactCurrency(pick)}
              </Text>
              <PickTimeline pick={pick} />
            </Grid>
          </Flex>
          <Pick pick={pick} />
        </Flex>
      ))}
    </Flex>
  );
}

function PickTimeline({ pick }: { pick: PickRow }) {
  const pickTeam = teams.find((t) => t.id === pick.teamId);
  if (!pickTeam) {
    throw new Error(`Team not found for pick with teamId: ${pick.teamId}`);
  }
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button key={pick.id} variant="ghost" size="sm" className="h-16 w-16">
          <History className={"h-8 w-8 text-primary"} />
        </Button>
      </DialogTrigger>

      <DialogContent
        className="left-[53%] max-h-[55rem] max-w-fit overflow-y-auto max-sm:left-[50%]
          max-sm:max-h-[30rem] max-sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>
            <Flex align="center" gap="sm">
              <TeamLogo
                size="sm"
                nbaTeamId={pickTeam?.TEAM_ID}
                leagueId={LeagueId.NBA}
              />
              {pickTeam?.TEAM_CITY}
            </Flex>
          </DialogTitle>
          <DialogDescription className="text-md font-semibold">
            {pick.year} {pickRoundFormat(pick.round)} Pick
          </DialogDescription>
        </DialogHeader>

        <Flex direction="down" gap="md">
          {pick.footnotes?.map((footnote) => {
            const footnoteTeamIds = footnote.teams.map(
              (team: { id: number }) => {
                const foundTeam = teams.find((t) => t.id === team.id);
                if (!foundTeam) {
                  throw new Error(
                    `Team not found for footnote with teamId: ${team.id}`
                  );
                }
                return foundTeam.TEAM_ID;
              }
            );

            return (
              <Flex key={pick.id} direction="down" gap="sm">
                <Flex direction="right">
                  <Flex direction="down" align="center" className="mr-2 h-full">
                    <div className="mb-2 mt-1 h-[0.4rem] w-[0.35rem] rounded-full bg-secondary-800" />
                    <div className="h-full w-[2px] border border-secondary-800" />
                  </Flex>
                  <Flex direction="down" gap="sm">
                    <Text size="xs" muted heading>
                      {dayjs(footnote.date).format("MMMM DD, YYYY")}
                    </Text>
                    <Flex gap="sm">
                      {footnoteTeamIds.map(
                        (teamId: number) =>
                          teamId && (
                            <TeamLogo
                              key={teamId}
                              size="xs"
                              nbaTeamId={teamId}
                              leagueId={LeagueId.NBA}
                            />
                          )
                      )}
                    </Flex>
                    <Text
                      className="max-w-[1000px] whitespace-pre-wrap break-words font-sans leading-6"
                      as="pre"
                      size="sm"
                    >
                      {footnote.note}
                    </Text>
                  </Flex>
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      </DialogContent>
    </Dialog>
  );
}
